export type LibraryFormat = "text" | "audio" | "video" | "unknown";
export type LibraryLane = "read" | "listen" | "watch";
export type LibraryWatchFilterMode =
  | "longer_than_threshold"
  | "all_video";
export type LibraryStatus =
  | "new"
  | "open"
  | "in_progress"
  | "finished"
  | "dismissed";
export type VectorStatus = "ready" | "pending" | "failed";
export type PrivateSummaryInputMode = "typed" | "spoken";
export type LibraryTriageConfidence =
  | "high"
  | "medium"
  | "requires_review";
export type ConversationState = "available" | "postponed" | "discussed";
export type ConversationDateFilter =
  | "all"
  | "week"
  | "two_weeks"
  | "month"
  | "custom";

export const DEFAULT_LONG_VIDEO_THRESHOLD_SECONDS = 4 * 60;
export const NATIVE_CONVERSATION_THEMES = [
  "interesting",
  "relationships",
  "health",
  "ai-tech",
  "funny",
  "money",
  "culture",
  "travel",
  "work",
  "housing",
  "politics",
  "safety",
  "sports",
  "food",
  "animals-nature",
  "self-development",
  "society",
] as const;

export interface AudienceAssignment {
  id: string;
  audienceId: string;
  audienceLabel: string;
  conversationEnabled: boolean;
  libraryEnabled: boolean;
  isPrimary: boolean;
  conversationState: ConversationState;
}

export interface PrivateCardMedia {
  kind: "image";
  url: string;
  alt: string;
}

export interface LibraryTriageDecision {
  detectedFormat: LibraryFormat;
  detectedLane: LibraryLane | null;
  format: LibraryFormat;
  lane: LibraryLane | null;
  ruleVersion: "library-triage-v1";
  confidence: LibraryTriageConfidence;
  requiresReview: boolean;
  longVideoThresholdSeconds: number;
  wasHumanCorrected: boolean;
}

export interface Bookmark {
  id: string;
  title: string;
  sourceLabel: string;
  sourceURL: string;
  senderID: string;
  senderLabel: string;
  audienceAssignments: AudienceAssignment[];
  libraryPublications: LibraryPublication[];
  format: LibraryFormat;
  lane: LibraryLane;
  createdAt: string;
  durationLabel?: string;
  videoDurationSeconds?: number;
  primaryTheme: string;
  cluster?: string;
  privateNotes: string;
  privateHumanSummary: string;
  privateSummaryInputMode?: PrivateSummaryInputMode | null;
  triageAudit?: LibraryTriageDecision | null;
  summaryFingerprint: string;
  embeddedSummaryFingerprint: string | null;
  vectorStatus: VectorStatus;
  keywords: string[];
  tags: string[];
  privateCardMedia?: PrivateCardMedia[];
}

export interface LibraryFilters {
  audienceId: string;
  senderID: string;
  lane: LibraryLane | "all";
  status: LibraryStatus | "all";
  watchMode?: LibraryWatchFilterMode;
  longVideoThresholdSeconds?: number;
}

export interface LibraryPublication {
  id: string;
  assignmentId: string;
  sharedByUserID: string;
  sharedTitle: string;
  sharedURL: string | null;
  sharedSourceLabel: string;
  sharedNotes: string;
  publishedAt: string;
  updatedAt: string;
  revokedAt: string | null;
  format: LibraryFormat;
  lane: LibraryLane;
  videoDurationSeconds?: number;
}

export interface LibraryReceipt {
  publicationId: string;
  userId: string;
  state: LibraryStatus;
  openedAt?: string;
  progressUpdatedAt?: string;
  finishedAt?: string;
  dismissedAt?: string;
  updatedAt: string;
}

export interface LibraryRecipientItem {
  id: string;
  audienceId: string;
  sharedByUserID: string;
  title: string;
  sourceURL: string | null;
  sourceLabel: string;
  sharedNotes: string;
  publishedAt: string;
  updatedAt: string;
  format: LibraryFormat;
  lane: LibraryLane;
  videoDurationSeconds?: number;
}

export interface LibraryRecipientEntry {
  item: LibraryRecipientItem;
  receipt: LibraryReceipt;
}

export interface LibraryCandidate {
  bookmark: Bookmark;
  assignment: AudienceAssignment;
  entry: LibraryRecipientEntry;
}

export interface BookmarkEdits {
  publicationID: string | null;
  sharedByUserID: string;
  title: string;
  sourceLabel: string;
  sourceURL: string;
  primaryTheme: string;
  privateCardMedia: PrivateCardMedia[];
  sharedTitle: string;
  sharedSourceLabel: string;
  sharedURL: string;
  sharedNotes: string;
  privateNotes: string;
  privateHumanSummary: string;
  triageAudit: LibraryTriageDecision | null;
  keywords: string[];
  tags: string[];
  format: LibraryFormat;
  lane: LibraryLane;
  videoDurationSeconds?: number;
  audienceAssignments: AudienceAssignment[];
}

export interface ConversationCard {
  bookmarkID: string;
  audienceId: string;
  audienceLabel: string;
  title: string;
  supportingText: string;
  sourceLabel: string;
  sourceURL: string;
  theme: string;
  createdAt: string;
  state: ConversationState;
  privateCardMedia: PrivateCardMedia[];
}

export interface ConversationFilters {
  themes: string[];
  dateFilter: ConversationDateFilter;
  customStart?: string;
  customEnd?: string;
}

export interface ConversationStats {
  available: number;
  discussed: number;
  postponed: number;
}

export function toLibraryRecipientItem(
  bookmark: Bookmark,
  audienceId: string,
): LibraryRecipientItem | null {
  const assignment = bookmark.audienceAssignments.find((candidate) => (
    candidate.audienceId === audienceId
    && candidate.libraryEnabled
  ));
  if (!assignment) return null;
  const livePublications = bookmark.libraryPublications.filter((publication) => (
    publication.assignmentId === assignment.id
    && publication.revokedAt === null
  ));
  if (livePublications.length !== 1) return null;
  const [publication] = livePublications;
  return {
    id: publication.id,
    audienceId: assignment.audienceId,
    sharedByUserID: publication.sharedByUserID,
    title: publication.sharedTitle,
    sourceURL: publication.sharedURL,
    sourceLabel: publication.sharedSourceLabel,
    sharedNotes: publication.sharedNotes,
    publishedAt: publication.publishedAt,
    updatedAt: publication.updatedAt,
    format: publication.format,
    lane: publication.lane,
    ...(publication.videoDurationSeconds === undefined
      ? {}
      : { videoDurationSeconds: publication.videoDurationSeconds }),
  };
}

export function toLibraryRecipientEntry(
  bookmark: Bookmark,
  audienceId: string,
  recipientUserID: string,
  receipts: LibraryReceipt[],
): LibraryRecipientEntry | null {
  const item = toLibraryRecipientItem(bookmark, audienceId);
  if (!item) return null;
  const matchingReceipts = receipts.filter((receipt) => (
    receipt.publicationId === item.id
    && receipt.userId === recipientUserID
  ));
  if (matchingReceipts.length !== 1) return null;
  return { item, receipt: matchingReceipts[0] };
}

export function filterLibrary(
  bookmarks: Bookmark[],
  receipts: LibraryReceipt[],
  recipientUserID: string,
  filters: LibraryFilters,
): LibraryCandidate[] {
  return bookmarks.flatMap((bookmark) => (
    bookmark.audienceAssignments.flatMap((assignment) => {
      if (
        !assignment.libraryEnabled
        || (
          filters.audienceId !== "all"
          && assignment.audienceId !== filters.audienceId
        )
      ) return [];
      const entry = toLibraryRecipientEntry(
        bookmark,
        assignment.audienceId,
        recipientUserID,
        receipts,
      );
      if (
        !entry
        || (
          filters.senderID !== "all"
          && entry.item.sharedByUserID !== filters.senderID
        )
        || !matchesLibraryLane(entry.item, filters)
        || (
          filters.status !== "all"
          && entry.receipt.state !== filters.status
        )
      ) return [];
      return [{ bookmark, assignment, entry }];
    })
  ));
}

export function filterRecipientLibrary(
  entries: LibraryRecipientEntry[],
  filters: LibraryFilters,
): LibraryRecipientEntry[] {
  return entries.filter(({ item, receipt }) => (
    (
      filters.audienceId === "all"
      || item.audienceId === filters.audienceId
    )
    && (
      filters.senderID === "all"
      || item.sharedByUserID === filters.senderID
    )
    && matchesLibraryLane(item, filters)
    && (
      filters.status === "all"
      || receipt.state === filters.status
    )
  ));
}

function matchesLibraryLane(
  item: LibraryRecipientItem,
  filters: LibraryFilters,
): boolean {
  switch (filters.lane) {
    case "all":
      return true;
    case "read":
    case "listen":
      return item.lane === filters.lane;
    case "watch":
      if (item.format !== "video") return false;
      if ((filters.watchMode ?? "longer_than_threshold") === "all_video") {
        return true;
      }
      return (
        (item.videoDurationSeconds ?? 0)
        > (
          filters.longVideoThresholdSeconds
          ?? DEFAULT_LONG_VIDEO_THRESHOLD_SECONDS
        )
      );
  }
}

export function conversationDeck(
  bookmarks: Bookmark[],
  audienceId: string,
): ConversationCard[] {
  return bookmarks.flatMap((bookmark) => {
    const assignment = bookmark.audienceAssignments.find((candidate) => (
      candidate.audienceId === audienceId && candidate.conversationEnabled
    ));
    if (!assignment) return [];
    return [{
      bookmarkID: bookmark.id,
      audienceId: assignment.audienceId,
      audienceLabel: assignment.audienceLabel,
      title: bookmark.privateNotes || bookmark.title,
      supportingText: bookmark.privateNotes ? bookmark.title : "",
      sourceLabel: bookmark.sourceLabel,
      sourceURL: bookmark.sourceURL,
      theme: bookmark.primaryTheme,
      createdAt: bookmark.createdAt,
      state: assignment.conversationState,
      privateCardMedia: bookmark.privateCardMedia ?? [],
    }];
  });
}

export function filterConversationCards(
  cards: ConversationCard[],
  filters: ConversationFilters,
  now = new Date(),
): ConversationCard[] {
  const selectedThemes = new Set(filters.themes);
  const boundary = dateBoundary(filters.dateFilter, now);
  const customStart = filters.customStart
    ? startOfLocalDay(filters.customStart)
    : null;
  const customEnd = filters.customEnd
    ? endOfLocalDay(filters.customEnd)
    : null;

  return cards.filter((card) => {
    if (selectedThemes.size && !selectedThemes.has(card.theme)) return false;
    const createdAt = new Date(card.createdAt);
    if (Number.isNaN(createdAt.valueOf())) return false;
    if (filters.dateFilter === "custom") {
      if (customStart && createdAt < customStart) return false;
      if (customEnd && createdAt >= customEnd) return false;
      return true;
    }
    return boundary ? createdAt >= boundary : true;
  });
}

export function conversationStats(cards: ConversationCard[]): ConversationStats {
  return {
    available: cards.filter(({ state }) => state !== "discussed").length,
    discussed: cards.filter(({ state }) => state === "discussed").length,
    postponed: cards.filter(({ state }) => state === "postponed").length,
  };
}

export function parseTerms(value: string): string[] {
  const seen = new Set<string>();
  const terms: string[] = [];
  for (const rawTerm of value.split(",")) {
    const term = rawTerm.trim();
    const normalized = term.toLocaleLowerCase();
    if (!term || seen.has(normalized)) continue;
    seen.add(normalized);
    terms.push(term);
  }
  return terms;
}

export function safeExternalURL(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:"
      ? url.href
      : null;
  } catch {
    return null;
  }
}

export function safeSameOriginMediaURL(
  value: string,
  origin: string,
): string | null {
  try {
    const expectedOrigin = new URL(origin).origin;
    const url = new URL(value, expectedOrigin);
    return (
      (url.protocol === "http:" || url.protocol === "https:")
      && url.origin === expectedOrigin
    )
      ? url.href
      : null;
  } catch {
    return null;
  }
}

export const SUMMARY_CLEAR_UNAVAILABLE_MESSAGE =
  "A saved Summary can be replaced, but not cleared yet. Enter a replacement while private suppression behavior is being finalized.";

export function humanizeConversationTheme(theme: string): string {
  return theme
    .trim()
    .replaceAll("-", " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => `${word[0]?.toUpperCase() ?? ""}${word.slice(1).toLowerCase()}`)
    .join(" ");
}

export function validateConversationTheme(theme: string): string | null {
  return theme.trim()
    ? null
    : "Choose a Conversation theme before saving.";
}

export function validatePrivateCardMedia(
  media: PrivateCardMedia[],
): string | null {
  if (media.length > 1) {
    return "Add only one private card image.";
  }
  const [image] = media;
  if (!image) return null;
  if (
    image.kind !== "image"
    || !image.url.trim()
    || !image.alt.trim()
  ) {
    return "Add a short image description before saving.";
  }
  return null;
}

export function validatePrivateSummaryTransition(
  previousSummary: string,
  nextSummary: string,
): string | null {
  return previousSummary.trim() && !nextSummary.trim()
    ? SUMMARY_CLEAR_UNAVAILABLE_MESSAGE
    : null;
}

export function applyBookmarkEdits(
  bookmark: Bookmark,
  edits: BookmarkEdits,
  editedAt = new Date().toISOString(),
): Bookmark {
  const summaryError = validatePrivateSummaryTransition(
    bookmark.privateHumanSummary,
    edits.privateHumanSummary,
  );
  if (summaryError) throw new Error(summaryError);
  const themeError = validateConversationTheme(edits.primaryTheme);
  if (themeError) throw new Error(themeError);
  const mediaError = validatePrivateCardMedia(edits.privateCardMedia);
  if (mediaError) throw new Error(mediaError);
  const privateHumanSummary = edits.privateHumanSummary.trim();
  const summaryChanged = privateHumanSummary !== bookmark.privateHumanSummary;
  const audienceAssignments = normalizeAudienceAssignments(
    edits.audienceAssignments,
  );
  const retainedAssignmentIDs = new Set(
    audienceAssignments.map(({ id }) => id),
  );
  const libraryAssignmentIDs = new Set(
    audienceAssignments
      .filter(({ libraryEnabled }) => libraryEnabled)
      .map(({ id }) => id),
  );
  const videoDurationSeconds = edits.format === "video"
    ? edits.videoDurationSeconds
    : undefined;
  const publications = bookmark.libraryPublications.map((publication) => {
    const remainsLive = (
      retainedAssignmentIDs.has(publication.assignmentId)
      && libraryAssignmentIDs.has(publication.assignmentId)
    );
    if (!remainsLive && publication.revokedAt === null) {
      return {
        ...publication,
        revokedAt: editedAt,
        updatedAt: editedAt,
      };
    }
    if (remainsLive && publication.revokedAt === null) {
      const isSelected = publication.id === edits.publicationID;
      const selectedSharedURL = safeExternalURL(edits.sharedURL);
      const sharedChanged = isSelected && (
        publication.sharedTitle !== edits.sharedTitle.trim()
        || publication.sharedURL !== selectedSharedURL
        || publication.sharedSourceLabel !== edits.sharedSourceLabel.trim()
        || publication.sharedNotes !== edits.sharedNotes.trim()
      );
      const triageChanged = (
        publication.format !== edits.format
        || publication.lane !== edits.lane
        || publication.videoDurationSeconds !== videoDurationSeconds
      );
      return {
        ...publication,
        ...(isSelected ? {
          sharedTitle: edits.sharedTitle.trim(),
          sharedURL: selectedSharedURL,
          sharedSourceLabel: edits.sharedSourceLabel.trim(),
          sharedNotes: edits.sharedNotes.trim(),
        } : {}),
        format: edits.format,
        lane: edits.lane,
        videoDurationSeconds,
        updatedAt: sharedChanged || triageChanged
          ? editedAt
          : publication.updatedAt,
      };
    }
    return publication;
  });

  for (const assignment of audienceAssignments) {
    if (!assignment.libraryEnabled) continue;
    const hasLivePublication = publications.some((publication) => (
      publication.assignmentId === assignment.id
      && publication.revokedAt === null
    ));
    if (hasLivePublication) continue;
    publications.push({
      id: opaquePublicationID(
        bookmark.id,
        assignment.audienceId,
        publications.length,
      ),
      assignmentId: assignment.id,
      sharedByUserID: edits.sharedByUserID.trim(),
      sharedTitle: edits.sharedTitle.trim(),
      sharedURL: safeExternalURL(edits.sharedURL),
      sharedSourceLabel: edits.sharedSourceLabel.trim(),
      sharedNotes: edits.sharedNotes.trim(),
      publishedAt: editedAt,
      updatedAt: editedAt,
      revokedAt: null,
      format: edits.format,
      lane: edits.lane,
      videoDurationSeconds,
    });
  }

  return {
    ...bookmark,
    title: edits.title.trim(),
    sourceLabel: edits.sourceLabel.trim(),
    sourceURL: safeExternalURL(edits.sourceURL) ?? "",
    primaryTheme: edits.primaryTheme.trim(),
    privateCardMedia: edits.privateCardMedia.map((media) => ({
      kind: "image",
      url: media.url.trim(),
      alt: media.alt.trim(),
    })),
    privateNotes: edits.privateNotes.trim(),
    privateHumanSummary,
    privateSummaryInputMode: privateHumanSummary
      ? summaryChanged
        ? "typed"
        : bookmark.privateSummaryInputMode ?? null
      : null,
    triageAudit: edits.triageAudit,
    summaryFingerprint: summaryChanged
      ? fingerprintPrivateSummary(privateHumanSummary)
      : bookmark.summaryFingerprint,
    embeddedSummaryFingerprint: summaryChanged
      ? null
      : bookmark.embeddedSummaryFingerprint,
    vectorStatus: summaryChanged ? "pending" : bookmark.vectorStatus,
    keywords: deduplicateTerms(edits.keywords),
    tags: deduplicateTerms(edits.tags),
    format: edits.format,
    lane: edits.lane,
    videoDurationSeconds,
    audienceAssignments,
    libraryPublications: publications,
  };
}

export function upsertLibraryReceipt(
  receipts: LibraryReceipt[],
  publicationId: string,
  userId: string,
  state: LibraryStatus,
  updatedAt = new Date().toISOString(),
): LibraryReceipt[] {
  const existingIndex = receipts.findIndex((receipt) => (
    receipt.publicationId === publicationId
    && receipt.userId === userId
  ));
  const existing = existingIndex < 0 ? null : receipts[existingIndex];
  if (!existing) {
    if (state !== "new") {
      throw new Error("A new Library receipt must begin New.");
    }
    return [...receipts, {
      publicationId,
      userId,
      state: "new",
      updatedAt,
    }];
  }
  if (
    !allowedLibraryStatusTransitions(existing.state).includes(state)
  ) {
    throw new Error(
      `Library progress cannot move from ${humanizeLibraryStatus(
        existing.state,
      )} to ${humanizeLibraryStatus(state)}.`,
    );
  }
  if (
    !isISODateAtOrAfter(updatedAt, existing.updatedAt)
  ) {
    throw new Error("Library progress updates must be chronological.");
  }

  const next: LibraryReceipt = {
    publicationId,
    userId,
    state,
    updatedAt,
  };
  if (state === "open") {
    next.openedAt = updatedAt;
  } else if (state === "in_progress") {
    if (!existing.openedAt) {
      throw new Error("In-progress Library receipts need an open timestamp.");
    }
    next.openedAt = existing.openedAt;
    next.progressUpdatedAt = updatedAt;
  } else if (state === "finished") {
    if (!existing.openedAt) {
      throw new Error("Finished Library receipts need an open timestamp.");
    }
    next.openedAt = existing.openedAt;
    if (existing.progressUpdatedAt) {
      next.progressUpdatedAt = existing.progressUpdatedAt;
    }
    next.finishedAt = updatedAt;
  } else if (state === "dismissed") {
    if (existing.openedAt) next.openedAt = existing.openedAt;
    if (existing.progressUpdatedAt) {
      next.progressUpdatedAt = existing.progressUpdatedAt;
    }
    next.dismissedAt = updatedAt;
  }
  return receipts.map((receipt, index) => (
    index === existingIndex ? next : receipt
  ));
}

export function allowedLibraryStatusTransitions(
  state: LibraryStatus,
): LibraryStatus[] {
  switch (state) {
    case "new":
      return ["open", "dismissed"];
    case "open":
      return ["in_progress", "finished", "dismissed"];
    case "in_progress":
      return ["in_progress", "finished", "dismissed"];
    case "finished":
    case "dismissed":
      return [];
  }
}

export function validatePublicationAlignment(
  bookmark: Bookmark,
): string | null {
  const assignmentIDs = new Set(
    bookmark.audienceAssignments.map(({ id }) => id),
  );
  const publicationIDs = new Set<string>();
  for (const publication of bookmark.libraryPublications) {
    if (publicationIDs.has(publication.id)) {
      return `Publication ${publication.id} is duplicated.`;
    }
    publicationIDs.add(publication.id);
    if (
      !publication.assignmentId.trim()
      || !assignmentIDs.has(publication.assignmentId)
    ) {
      return `Publication ${publication.id} needs a known audience assignment.`;
    }
    if (!publication.id.trim() || publication.id !== publication.id.trim()) {
      return "Library publication IDs must be explicit and trimmed.";
    }
    if (
      !publication.sharedByUserID.trim()
      || publication.sharedByUserID !== publication.sharedByUserID.trim()
    ) {
      return `Publication ${publication.id} needs an explicit publisher.`;
    }
    if (
      !publication.sharedTitle.trim()
      || publication.sharedTitle !== publication.sharedTitle.trim()
    ) {
      return `Publication ${publication.id} needs an explicit shared title.`;
    }
    if (
      !publication.sharedSourceLabel.trim()
      || publication.sharedSourceLabel !== publication.sharedSourceLabel.trim()
    ) {
      return `Publication ${publication.id} needs an explicit shared source label.`;
    }
    if (validateVideoDurationSeconds(publication.videoDurationSeconds)) {
      return `Publication ${publication.id} needs a nonnegative whole-second video duration.`;
    }
  }
  for (const assignment of bookmark.audienceAssignments) {
    const liveCount = bookmark.libraryPublications.filter((publication) => (
      publication.assignmentId === assignment.id
      && publication.revokedAt === null
    )).length;
    if (assignment.libraryEnabled && liveCount !== 1) {
      return `Audience ${assignment.audienceLabel} needs exactly one live Library publication.`;
    }
    if (!assignment.libraryEnabled && liveCount !== 0) {
      return `Conversation-only audience ${assignment.audienceLabel} cannot have a live Library publication.`;
    }
  }
  return null;
}

export function updateConversationState(
  bookmark: Bookmark,
  audienceId: string,
  state: ConversationState,
): Bookmark {
  return {
    ...bookmark,
    audienceAssignments: bookmark.audienceAssignments.map((assignment) => (
      assignment.audienceId === audienceId && assignment.conversationEnabled
        ? { ...assignment, conversationState: state }
        : assignment
    )),
  };
}

export function validateAudienceAssignments(
  assignments: AudienceAssignment[],
): string | null {
  const retained = assignments.filter(
    ({ conversationEnabled, libraryEnabled }) => (
      conversationEnabled || libraryEnabled
    ),
  );
  if (!retained.length) {
    return "Choose at least one Private card or Shared Library destination.";
  }
  const seen = new Set<string>();
  for (const assignment of assignments) {
    if (seen.has(assignment.audienceId)) {
      return `Audience ${assignment.audienceLabel} is assigned more than once.`;
    }
    seen.add(assignment.audienceId);
  }
  const primaryAssignments = assignments.filter(({ isPrimary }) => isPrimary);
  if (primaryAssignments.length !== 1) {
    return "Choose exactly one default audience.";
  }
  const [primary] = primaryAssignments;
  if (!primary.conversationEnabled && !primary.libraryEnabled) {
    return "The default audience needs a Private card or Shared Library destination.";
  }
  return null;
}

export function validateSharedTitle(
  sharedTitle: string,
  assignments: AudienceAssignment[],
): string | null {
  if (
    assignments.some(({ libraryEnabled }) => libraryEnabled)
    && !sharedTitle.trim()
  ) {
    return "Add a shared title before placing this bookmark in Library.";
  }
  return null;
}

export function validateOwnerTitle(title: string): string | null {
  return title.trim()
    ? null
    : "Add a private bookmark title before saving.";
}

export function validateOptionalExternalURL(
  value: string,
  fieldLabel: string,
): string | null {
  if (!value.trim() || safeExternalURL(value)) return null;
  return `Enter a full http:// or https:// URL for ${fieldLabel}, or leave it blank.`;
}

export function validateVideoDurationSeconds(
  value: number | undefined,
): string | null {
  return (
    value === undefined
    || (Number.isInteger(value) && value >= 0)
  )
    ? null
    : "Enter a nonnegative video length, or leave it blank.";
}

export function validateSharedSourceLabel(
  sharedSourceLabel: string,
  assignments: AudienceAssignment[],
): string | null {
  if (
    assignments.some(({ libraryEnabled }) => libraryEnabled)
    && !sharedSourceLabel.trim()
  ) {
    return "Add a shared source label before placing this bookmark in Library.";
  }
  return null;
}

export function fingerprintPrivateSummary(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `summary-v1-${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

function normalizeAudienceAssignments(
  assignments: AudienceAssignment[],
): AudienceAssignment[] {
  const retained = assignments.filter(
    ({ conversationEnabled, libraryEnabled }) => (
      conversationEnabled || libraryEnabled
    ),
  );
  const primaryIndex = retained.findIndex(({ isPrimary }) => isPrimary);
  return retained
    .map((assignment, index) => ({
      ...assignment,
      isPrimary: index === (primaryIndex < 0 ? 0 : primaryIndex),
    }))
    .sort((left, right) => left.audienceLabel.localeCompare(right.audienceLabel));
}

function opaquePublicationID(
  bookmarkID: string,
  audienceID: string,
  generation: number,
): string {
  return `publication-${audienceID}-${shortOpaqueHash(
    `${bookmarkID}:${audienceID}:${generation}`,
  )}`;
}

function shortOpaqueHash(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36).padStart(7, "0");
}

function dateBoundary(
  filter: ConversationDateFilter,
  now: Date,
): Date | null {
  const daysByFilter: Partial<Record<ConversationDateFilter, number>> = {
    week: 7,
    two_weeks: 14,
    month: 30,
  };
  const days = daysByFilter[filter];
  if (!days) return null;
  const boundary = new Date(now);
  boundary.setDate(boundary.getDate() - days);
  return boundary;
}

function startOfLocalDay(value: string): Date | null {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.valueOf()) ? null : date;
}

function endOfLocalDay(value: string): Date | null {
  const date = startOfLocalDay(value);
  if (!date) return null;
  date.setDate(date.getDate() + 1);
  return date;
}

function deduplicateTerms(terms: string[]): string[] {
  const seen = new Set<string>();
  return terms.flatMap((rawTerm) => {
    const term = rawTerm.trim();
    const normalized = term.toLocaleLowerCase();
    if (!term || seen.has(normalized)) return [];
    seen.add(normalized);
    return [term];
  });
}

function humanizeLibraryStatus(state: LibraryStatus): string {
  return state === "in_progress"
    ? "In progress"
    : `${state.slice(0, 1).toUpperCase()}${state.slice(1)}`;
}

function isISODateAtOrAfter(value: string, boundary: string): boolean {
  const date = new Date(value);
  const boundaryDate = new Date(boundary);
  return (
    !Number.isNaN(date.valueOf())
    && !Number.isNaN(boundaryDate.valueOf())
    && date >= boundaryDate
  );
}
